// Rights (parts/Rights.body): the Sports Rights Consolidated Master, columns A to Q (D101);
// held means the status begins "Held"; the territory as written with the ISO code Cobrain
// resolved (N5); joined to Snowflake's competitions once a person confirms the link (module 07).
import { ActionButton } from '../parts/index.js';
import { useState } from 'react';
import { records, type RightsFilter } from '../api.js';
import { formatDate } from '../labels.js';
import { Count, Empty, Facet, Failed, Foot, Heading, Loading, orText, Pill, RowLink, useLoad, type PageProps } from './shared.js';

export function Rights({ store, open }: PageProps) {
  const [filter, setFilterOnly] = useState<RightsFilter>('all');
  const [sport, setSportOnly] = useState('');
  const [territory, setTerritoryOnly] = useState('');
  const [holder, setHolderOnly] = useState('');
  // BUGS.md D132: the list pages at its foot with Previous and Next, as People does; a new filter starts at the first page.
  const [offset, setOffset] = useState(0);
  const from0 = <T,>(set: (v: T) => void) => (v: T) => { set(v); setOffset(0); };
  const setFilter = from0(setFilterOnly);
  const setSport = from0(setSportOnly);
  const setTerritory = from0(setTerritoryOnly);
  const setHolder = from0(setHolderOnly);
  const [state] = useLoad(() => records.rights(store.tenantId, { filter, sport, territory, holder, offset }), [store.tenantId, filter, sport, territory, holder, offset]);
  const counts = state.kind === 'ready' ? state.data.counts : null;
  return (
    <div className="recs">
      <Heading title="Rights" line={<>
        <Count n={counts?.all}>on record</Count>
        <Count n={counts?.held}>held</Count>
        <Count n={counts?.available}>available</Count>
      </>} />
      <div className="filters">
        <div className="chips">
          <span className="chips-l">Show</span>
        <Pill on={filter === 'all'} onClick={() => setFilter('all')}>All</Pill>
        <Pill on={filter === 'held'} onClick={() => setFilter('held')} n={counts?.held}>Held</Pill>
        <Pill on={filter === 'available'} onClick={() => setFilter('available')} n={counts?.available}>Available</Pill>
        <Pill on={filter === 'in_discussion'} onClick={() => setFilter('in_discussion')} n={counts?.inDiscussion}>In discussion</Pill>
        <Pill on={filter === 'from_2027'} onClick={() => setFilter('from_2027')} n={counts?.from2027}>Available from 2027</Pill>
        <Facet label="By sport" value={sport} options={state.kind === 'ready' ? state.data.sports : []} onChange={setSport} />
        <Facet label="By territory" value={territory} options={state.kind === 'ready' ? state.data.territories : []} onChange={setTerritory} />
        <Facet label="By rights holder" value={holder} options={state.kind === 'ready' ? state.data.holders : []} onChange={setHolder} />
        </div>
      </div>
      {state.kind === 'loading' ? <Loading what="the rights" /> : state.kind === 'error' ? <Failed message={state.message} /> : (
        <div className="scroll"><table className="rec">
          <thead><tr><th>Sport</th><th>Property</th><th>Rights holder</th><th>Territory</th><th>Tier</th><th className="num">Video p.a.</th><th className="num">Data p.a.</th><th>Status</th><th>From</th><th>Term / expiry</th><th>Usage data</th></tr></thead>
          <tbody>
            {state.data.rights.length === 0
              ? <Empty cols={11} head="No property under that." says="Choose another filter, or All. Properties come from the rights master; each one carries its territory as written and the competition it matches in the usage data." />
              : null}
            {state.data.rights.map((r) => (
              <tr key={r.id} className="row-open" onClick={() => open(`/rights/${r.id}`)}>
                <td>{r.sport}{r.isEsports && r.sport.toLowerCase() !== 'esports' ? <> <span className="note">e-sports</span></> : null}</td>
                <td><b><RowLink to={`/rights/${r.id}`} open={open}>{r.propertyName}</RowLink></b></td>
                <td>{r.rightsHolderOrgId && r.rightsHolderOrgName ? <ActionButton reading type="button" className="dotted" onClick={(e) => { e.stopPropagation(); open(`/companies/${r.rightsHolderOrgId}`); }}>{r.rightsHolderOrgName}</ActionButton> : orText(r.rightsHolderName)}</td>
                <td>{orText(r.territory)}{r.territoryIso ? ` (${r.territoryIso})` : ''}</td>
                <td>{orText(r.domesticTier)}</td>
                <td className="num">{r.matchesVideoPa ?? ''}</td>
                <td className="num">{r.matchesDataPa ?? ''}</td>
                <td>{r.status}</td>
                <td className="when">{r.availableFrom ? formatDate(r.availableFrom) : ''}</td>
                <td className="when">{r.termExpiry ? formatDate(r.termExpiry) : ''}</td>
                <td>{r.competition ? <>{r.competition.confirmed ? 'Linked' : 'Suggested'} <span className="note">{r.competition.snowflakeCompetition ?? r.competition.oneplatformCompetitionId}</span></> : <span className="note">Not linked</span>}</td>
              </tr>
            ))}
            {/* An older API sends no page: it says how many it drew of the store, as before. */}
            {state.data.limit !== undefined && state.data.total !== undefined
              ? <Foot cols={11} offset={state.data.offset ?? 0} limit={state.data.limit} shown={state.data.rights.length} total={state.data.total} onPage={setOffset} />
              : <Foot cols={11} offset={0} limit={state.data.rights.length} total={counts?.all ?? state.data.rights.length} onPage={null} />}
          </tbody>
        </table></div>
      )}
    </div>
  );
}
